import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

// --------------------------------------------------
// FETCH LIST (with pagination, sorting, filters)
// --------------------------------------------------
export const fetchMakes = createAsyncThunk(
  "make/fetchMakes",
  async (
    {
      page,
      pageSize,
      sort,
      order,
      search,
      filters,
    }: {
      page: number;
      pageSize: number;
      search?: string;
      sort?: string;
      order?: "asc" | "desc" | "";
      filters?: any;
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams();

      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());

      if (search) params.append("search", search.trim());

      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.MAKE?.LIST}?${params.toString()}`,
        body,
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch Make list");
    }
  },
);

// --------------------------------------------------
// GET BY ID
// --------------------------------------------------
export const fetchMakeById = createAsyncThunk(
  "Make/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.MAKE?.DETAIL(id)}`,
      );
      return response.data?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Make by ID",
      );
    }
  },
);

// --------------------------------------------------
// DROPDOWN
// --------------------------------------------------
export const fetchMakeByDropDown = createAsyncThunk(
  "make/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.MAKE?.DROPDOWN}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch Make list");
    }
  },
);

// --------------------------------------------------
// UPDATE
// --------------------------------------------------
export const updateMake = createAsyncThunk(
  "make/updateMake",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT?.MASTERS?.MAKE?.UPDATE(data?.make_id)}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update Make");
    }
  },
);

// --------------------------------------------------
// UPDATE STATUS
// --------------------------------------------------
export const updateStatusInMake = createAsyncThunk(
  "make/updateStatusInMake",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await Axios.patch(
        `${API_ENDPOINT?.MASTERS?.MAKE?.STATUS_UPDATE(data?.make_id)}`,
        payload,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in Make",
      );
    }
  },
);

// --------------------------------------------------
// CREATE
// --------------------------------------------------
export const addMake = createAsyncThunk(
  "make/addMake",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.MAKE?.CREATE}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.response?.data || "Failed to add Make",
      );
    }
  },
);

// --------------------------------------------------
// DELETE
// --------------------------------------------------
export const deleteMake = createAsyncThunk(
  "make/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      await Axios.delete(`${API_ENDPOINT?.MASTERS?.MAKE?.DELETE}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

// --------------------------------------------------
// EXPORT CSV / EXCEL
// --------------------------------------------------
export const exportMakeCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT?.MASTERS?.MAKE?.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `makes_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
